import {ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Feature} from 'ol';
import {FeatureLike} from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import {Geometry} from 'ol/geom';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import {EMPTY, Subject, catchError, debounceTime, forkJoin, map, switchMap, take, tap} from 'rxjs';
import {AppService} from '../../../../services/app.service';
import {EdgarPipelineService, WeatherCounty, WeatherMetadata} from '../../../../services/edgar-pipeline.service';
import {MapService} from '../../../../services/map.service';
import {selectCounties} from '../../../../state/location/location.reducer';
import {BaseUILayer} from '../../../classes/layers/base-ui-layer';
import {WeatherOutagePopupComponent} from '../../../layer-popups/weather-outage-popup/weather-outage-popup.component';
import {LayerHeaderComponent} from '../../layer-header/layer-header.component';
import {WEATHER_CATEGORY_COLORS} from '../../../utilities/weather-category';

interface MetricOption {key: string; label: string; shortLabel: string; percent?: boolean; category?: boolean; impact?: boolean}
interface LegendItem {color: string; label: string}

const SCALE = ['#eff2e8', '#cfdcbf', '#9ac69f', '#559b79', '#1d6650'];
const NO_DATA_COLOR = '#d9d9d4';
const LOW_SUPPORT_COLOR = '#eee8dc';

@Component({
  selector: 'tgrid-weather-outage-layer',
  imports: [DecimalPipe, LayerHeaderComponent, MatCheckboxModule, MatFormFieldModule, MatSelectModule, ReactiveFormsModule],
  templateUrl: './weather-outage-layer.component.html',
  styleUrl: './weather-outage-layer.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherOutageLayerComponent extends BaseUILayer<BaseLayer> {
  private readonly appService = inject(AppService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pipeline = inject(EdgarPipelineService);
  private readonly store = inject(Store);
  private readonly loadRequests = new Subject<void>();

  protected readonly metadata = signal<WeatherMetadata | null>(null);
  protected readonly loading = signal(false);
  protected readonly mappedCount = signal(0);
  protected readonly visibleCount = signal(0);
  protected readonly legendItems = signal<LegendItem[]>([]);
  protected readonly metrics: MetricOption[] = [
    {key: 'dominantCategory', label: 'Most frequent weather category', shortLabel: 'Most frequent category', category: true},
    {key: 'outageAssociatedCategory', label: 'Most outage-associated weather', shortLabel: 'Most frequent outage-associated weather', category: true},
    {key: 'highestMedianPeakCategory', label: 'Highest median peak category', shortLabel: 'Highest median peak weather', category: true, impact: true},
    {key: 'longestMedianRecoveryCategory', label: 'Longest recovery category', shortLabel: 'Longest median recovery weather', category: true, impact: true},
    {key: 'highestMedianBurdenCategory', label: 'Highest customer-minute burden', shortLabel: 'Highest median burden weather', category: true, impact: true},
    {key: 'episodes', label: 'Weather episodes', shortLabel: 'Weather episodes'},
    {key: 'episodesPerYear', label: 'Weather episodes per year', shortLabel: 'Episodes / year'},
    {key: 'associatedEvents', label: 'Associated outage events', shortLabel: 'Associated outage events'},
    {key: 'associationRate', label: 'Outage association rate', shortLabel: 'Association rate', percent: true},
    {key: 'medianPeak', label: 'Median peak outage', shortLabel: 'Median peak', percent: true, impact: true},
    {key: 'medianRecovery', label: 'Median recovery (minutes)', shortLabel: 'Median recovery', impact: true},
    {key: 'medianBurden', label: 'Median customer-minute burden', shortLabel: 'Median burden', impact: true},
    {key: 'compoundShare', label: 'Compound weather share', shortLabel: 'Compound share', percent: true},
  ];
  protected readonly filterGroup = new FormGroup({
    metric: new FormControl<string>('dominantCategory', {nonNullable: true}),
    category: new FormControl<string>('All', {nonNullable: true}),
    includeLowSupport: new FormControl<boolean>(false, {nonNullable: true}),
  });

  public constructor() {
    super();
    this.loadRequests.pipe(
      tap(() => this.loading.set(true)),
      switchMap(() => {
        const selectedStateIds = this.appService.selectedStates().map(state => state.id);
        const selectedStateAbbreviations = this.appService.selectedStates().map(state => state.abbreviation);
        const category = this.filterGroup.controls.category.value;
        const metric = this.metrics.find(option => option.key === this.filterGroup.controls.metric.value) ?? this.metrics[0];
        return forkJoin({
          response: this.pipeline.getWeatherCounties(category, selectedStateAbbreviations),
          counties: this.store.select(selectCounties).pipe(take(1)),
        }).pipe(
          map(({response, counties}) => {
            const byFips = new Map<string, WeatherCounty>(response.items.map(item => [item.fips, item]));
            this.mappedCount.set(response.items.length);
            const visibleItems = response.items.filter(item => this.hasSupport(item));
            this.visibleCount.set(visibleItems.length);
            const thresholds = this.buildLegend(visibleItems);
            return {
              thresholds,
              counties: counties
                .filter(county => selectedStateIds.length === 0 || selectedStateIds.includes(county.stateId))
                .map(county => {
                  const weatherData = byFips.get(county.geoid) ?? null;
                  return {
                    type: 'Feature',
                    geometry: JSON.parse(county.boundary) as unknown,
                    properties: {
                      ...county,
                      popoverComponent: WeatherOutagePopupComponent,
                      weatherData,
                      weatherMetricLabel: metric.shortLabel,
                      weatherMetricDisplayValue: weatherData ? this.formatMetricValue(weatherData, metric) : '—',
                      selectedWeatherCategory: category,
                      supported: weatherData ? this.hasSupport(weatherData) : false,
                    },
                  };
                }),
            };
          }),
          catchError(() => {
            this.loading.set(false);
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({counties, thresholds}) => {
      this.loading.set(false);
      const features = new GeoJSON().readFeatures({type: 'FeatureCollection', features: counties});
      const style = (feature: FeatureLike) => {
        const data = feature.get('weatherData') as WeatherCounty | null;
        const supported = feature.get('supported') as boolean;
        return new Style({
          stroke: new Stroke({color: '#202939', width: 0.75}),
          fill: new Fill({color: !data ? NO_DATA_COLOR : !supported ? LOW_SUPPORT_COLOR : this.color(data, thresholds)}),
        });
      };
      const existing = this.mapLayer().renderedLayer as VectorLayer<VectorSource> | undefined;
      if (existing && this.mapService.isLayerOnMap(existing)) {
        existing.setStyle(style);
        existing.getSource()?.clear();
        existing.getSource()?.addFeatures(features as Feature<Geometry>[]);
        return;
      }
      const layer = new VectorLayer({
        source: new VectorSource({features: features as Feature<Geometry>[], attributions: this.mapLayer().buildAttribution()}),
        properties: {name: this.mapLayer().displayName},
        opacity: this.opacity / 100,
        style,
      });
      this.mapLayer().renderedLayer = layer;
      MapService.getMapRef()?.addLayer(layer);
    });
    this.pipeline.getWeatherMetadata().pipe(takeUntilDestroyed()).subscribe(value => this.metadata.set(value));
    this.filterGroup.valueChanges.pipe(debounceTime(80), takeUntilDestroyed()).subscribe(() => {
      if (this.checked) this.getData();
    });
    effect(() => {
      this.appService.selectedStates();
      if (this.checked) this.getData();
    });
  }

  public override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (checked) this.getData();
    else if (this.mapLayer().renderedLayer) MapService.getMapRef()?.removeLayer(this.mapLayer().renderedLayer);
  }

  public override onDestroy(): void {
    if (this.mapLayer().renderedLayer) MapService.getMapRef()?.removeLayer(this.mapLayer().renderedLayer);
  }

  public override changeOpacity(opacity: number): void {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource> | undefined)?.setOpacity(opacity / 100);
  }

  protected currentMetricShortLabel(): string {
    return this.metrics.find(metric => metric.key === this.filterGroup.controls.metric.value)?.shortLabel ?? this.metrics[0].shortLabel;
  }

  private getData(): void {
    this.loadRequests.next();
  }

  private hasSupport(item: WeatherCounty): boolean {
    if (this.filterGroup.controls.includeLowSupport.value) return true;
    const metric = this.metrics.find(option => option.key === this.filterGroup.controls.metric.value);
    const support = metric?.impact ? item.metrics.impactSupport : item.metrics.weatherSupport;
    return support === 'ADEQUATE_SUPPORT';
  }

  private metricValue(item: WeatherCounty): number | string | null {
    const key = this.filterGroup.controls.metric.value;
    if (key in item.metrics) return item.metrics[key as keyof typeof item.metrics] as number | string | null;
    return item[key as keyof WeatherCounty] as number | string | null;
  }

  private formatMetricValue(item: WeatherCounty, option: MetricOption): string {
    const value = this.metricValue(item);
    if (value == null || value === '') return '—';
    if (typeof value === 'string') return value;
    if (option.percent) return `${(value * 100).toFixed(1)}%`;
    return value.toLocaleString(undefined, {maximumFractionDigits: 1});
  }

  private color(item: WeatherCounty, thresholds: number[]): string {
    const value = this.metricValue(item);
    if (typeof value === 'string') return WEATHER_CATEGORY_COLORS[value] ?? '#8d8374';
    if (typeof value !== 'number' || !Number.isFinite(value)) return NO_DATA_COLOR;
    const index = thresholds.findIndex(threshold => value <= threshold);
    return SCALE[index < 0 ? SCALE.length - 1 : index];
  }

  private buildLegend(items: WeatherCounty[]): number[] {
    const option = this.metrics.find(metric => metric.key === this.filterGroup.controls.metric.value);
    if (option?.category) {
      const values = [...new Set(items.map(item => this.metricValue(item)).filter((value): value is string => typeof value === 'string'))];
      this.legendItems.set(values.sort().map(value => ({color: WEATHER_CATEGORY_COLORS[value] ?? '#8d8374', label: value}))
        .concat({color: LOW_SUPPORT_COLOR, label: 'Low-support estimate'}, {color: NO_DATA_COLOR, label: 'No data'}));
      return [];
    }
    const values = items.map(item => this.metricValue(item)).filter((value): value is number => typeof value === 'number' && Number.isFinite(value)).sort((a, b) => a - b);
    if (values.length === 0) {
      this.legendItems.set([{color: NO_DATA_COLOR, label: 'No data'}]);
      return [];
    }
    const thresholds = SCALE.map((_color, index) => values[Math.min(values.length - 1, Math.floor(values.length * (index + 1) / SCALE.length) - 1)]);
    const format = (value: number) => option?.percent ? `${(value * 100).toFixed(1)}%` : value.toLocaleString(undefined, {maximumFractionDigits: 1});
    this.legendItems.set(SCALE.map((color, index) => ({
      color,
      label: index === 0 ? `≤ ${format(thresholds[index])}` : `${format(thresholds[index - 1])} – ${format(thresholds[index])}`,
    })).concat({color: LOW_SUPPORT_COLOR, label: 'Low-support estimate'}, {color: NO_DATA_COLOR, label: 'No data'}));
    return thresholds;
  }
}
